// Mail V2 RecipientsChips (2026-07-24): shared chip input component.
//
// 被 MailRecipientsFields (new mail) 与 MailV2Detail (reply pane) 复用。
//
// 单一数据源 = `value` (string, draftHook.fields.to/cc):
//   - 内部唯一 state = `draft` (未确定输入)
//   - chips 是派生:recipientsToChips(value) 每 render 重算
//   - onChange 总是回写 canonical comma-separated string
//   - 外部 setField("to", "a@x.com") → 自动渲染成 1 chip
//
// 行为:
//   - Enter / `,` / `;` / Tab / blur → commit draft → 触发 onChange
//   - Backspace on empty input → 删除末尾 valid chip(invalid 保留以可视化)
//   - Escape → 清空 draft
//   - Paste 检测 [,\n;\t] → preventDefault + commit 一组
//   - chip × 点击 → 删除单个 chip

import { useCallback, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import {
  parseRecipientText,
  recipientsToChips,
  serializedChipsToString,
  dedupeChips,
  type RecipientChip,
} from "./parseRecipients";

export interface RecipientsChipsProps {
  /** 单一数据源:draftHook.fields.to/cc (string) */
  value: string;
  /** 值变化(总是回写 canonical comma-separated string) */
  onChange: (next: string) => void;
  /** 必填,占位用 label("To" / "Cc") */
  label: string;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  dataTestid?: string;
  autoFocus?: boolean;
}

const SEPARATOR_RE = /[,;\n\t]/;

export function RecipientsChips({
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  ariaLabel,
  dataTestid,
  autoFocus = false,
}: RecipientsChipsProps) {
  // 内部唯一 state:未确定输入(Enter/blur/paste 才 commit)
  const [draft, setDraft] = useState<string>("");
  // 派生 chips (每 render 重算;操作频率低,不需要 useMemo)
  const chips: RecipientChip[] = recipientsToChips(value);

  // commit:把 draft 解析后并入现有 chips,回写 onChange
  const commit = useCallback(
    (text: string) => {
      if (!text || !text.trim()) {
        setDraft("");
        return;
      }
      const newChips = parseRecipientText(text);
      // dedupe:与现有 chips 合并,valid 按 email 去重
      const merged = dedupeChips([...chips, ...newChips]);
      onChange(serializedChipsToString(merged));
      setDraft("");
    },
    [chips, onChange],
  );

  // 删除单个 chip
  const removeChip = useCallback(
    (key: string) => {
      const next = chips.filter((c) => c.key !== key);
      onChange(serializedChipsToString(next));
    },
    [chips, onChange],
  );

  // Backspace on empty input → 删除末尾 valid chip
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === "," || e.key === ";") {
        e.preventDefault();
        commit(draft);
        return;
      }
      if (e.key === "Tab") {
        // Tab = blur 行为,commit 后不阻止 default (允许 tab 切焦点)
        if (draft.trim()) {
          commit(draft);
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setDraft("");
        return;
      }
      if (e.key === "Backspace" && draft === "") {
        // 找最后一个 valid chip 删除;invalid 不删(error state 保留)
        const lastValid = [...chips].reverse().find((c) => !c.invalid);
        if (lastValid) {
          e.preventDefault();
          removeChip(lastValid.key);
        }
        return;
      }
    },
    [commit, draft, chips, removeChip],
  );

  // Paste:检测分隔符 → preventDefault + commit 一组
  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      const pasted = e.clipboardData.getData("text");
      if (pasted && SEPARATOR_RE.test(pasted)) {
        e.preventDefault();
        commit(draft + pasted);
      }
      // 否则走默认 paste 行为(单地址 paste 到 input,用户再按 Enter 触发 commit)
    },
    [commit, draft],
  );

  // Blur:未 commit 的 draft 强制 commit
  const handleBlur = useCallback(() => {
    if (draft.trim()) {
      commit(draft);
    }
  }, [commit, draft]);

  return (
    <div
      className="mv2-recipients-chips"
      data-testid={dataTestid}
      role="group"
      aria-label={ariaLabel ?? label}
    >
      {chips.map((chip) => {
        const label = chip.displayName ?? chip.email;
        return (
          <span
            key={chip.key}
            className={
              "mv2-recipient-chip" +
              (chip.invalid ? " mv2-recipient-chip-invalid" : "")
            }
            title={chip.invalid ? `無効: ${chip.invalidReason ?? ""} (${chip.raw ?? ""})` : chip.email}
          >
            <span className="mv2-recipient-chip-label">{label}</span>
            <button
              type="button"
              className="mv2-recipient-chip-remove"
              aria-label={`${label} を削除`}
              title="削除"
              onClick={() => removeChip(chip.key)}
              disabled={disabled}
            >
              ×
            </button>
          </span>
        );
      })}
      <input
        type="text"
        className="mv2-recipients-input"
        placeholder={chips.length > 0 ? "" : (placeholder ?? `${label} を入力`)}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={ariaLabel ?? label}
        data-testid={dataTestid ? `${dataTestid}-input` : undefined}
      />
    </div>
  );
}
